import { useCallback, useState } from "react";
import {
    OrderStatus,
    StatusBadge,
} from "../Components/StatusBadge";
import "./OrderList.css";

export type MonitorOrderSummary = {
  id: string;
  accountId: string;
  accountName: string;
  automaticOrderDate: string;
  orderDate?: string | null;
  status: string;
  itemCount: number;
  updatedAt: string;
};

type OrderListFilter = {
  status?: string;
  search?: string;
  page?: number;
};

type OrderListProps = {
  orders: MonitorOrderSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error?: string | null;
  selectedId?: string | null;
  onSelect: (orderId: string) => void;
  onFilterChange: (filter: OrderListFilter) => void;
  onRetry: () => void;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
};

const MAX_SELECTION = 50;

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Alle" },
  { value: "pending", label: "Afventer" },
  { value: "sent", label: "Sendt" },
  { value: "completed", label: "Gennemført" },
  { value: "failed", label: "Fejlet" },
];

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "—";
  // Handle YYYYMMDD format
  if (/^\d{8}$/.test(dateStr)) {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${day}/${month}/${year}`;
  }
  // Handle unix timestamp (ms or seconds)
  if (/^\d{10,13}$/.test(dateStr)) {
    const ts = dateStr.length <= 10 ? Number(dateStr) * 1000 : Number(dateStr);
    const date = new Date(ts);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("da-DK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const OrderList = ({
  orders,
  totalCount,
  page,
  pageSize,
  loading,
  error,
  selectedId,
  onSelect,
  onFilterChange,
  onRetry,
  selectedIds = [],
  onSelectionChange,
}: OrderListProps) => {
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setStatusFilter(value);
      onFilterChange({ status: value || undefined, search: searchTerm || undefined, page: 1 });
    },
    [onFilterChange, searchTerm]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      onFilterChange({ status: statusFilter || undefined, search: value || undefined, page: 1 });
    },
    [onFilterChange, statusFilter]
  );

  const handleCheckboxChange = useCallback(
    (orderId: string, checked: boolean) => {
      if (!onSelectionChange) return;

      if (checked) {
        if (selectedIds.length >= MAX_SELECTION) return;
        onSelectionChange([...selectedIds, orderId]);
      } else {
        onSelectionChange(selectedIds.filter((id) => id !== orderId));
      }
    },
    [onSelectionChange, selectedIds]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;

      if (checked) {
        const newIds = orders
          .map((o) => o.id)
          .slice(0, MAX_SELECTION);
        onSelectionChange(newIds);
      } else {
        onSelectionChange([]);
      }
    },
    [onSelectionChange, orders]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      onFilterChange({ status: statusFilter || undefined, search: searchTerm || undefined, page: newPage });
    },
    [onFilterChange, statusFilter, searchTerm]
  );

  const totalPages = Math.ceil(totalCount / pageSize);
  const allSelected = orders.length > 0 && orders.every((o) => selectedIds.includes(o.id));

  // Error state
  if (error && !loading) {
    return (
      <div className="OrderList">
        <div className="OrderList__table-wrapper">
          <div className="OrderList__error">
            <div className="OrderList__error-message">
              Kunne ikke hente ordrer. Prøv igen.
            </div>
            <button className="OrderList__retry-btn" onClick={onRetry}>
              Prøv igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="OrderList">
      <div className="OrderList__toolbar">
        <input
          className="OrderList__search"
          type="text"
          placeholder="Søg efter konto-ID eller ordre-ID..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <select
          className="OrderList__filter"
          value={statusFilter}
          onChange={handleStatusChange}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {selectedIds.length > 0 && (
          <span className="OrderList__selection-count">
            {selectedIds.length} af {MAX_SELECTION} valgt
          </span>
        )}
      </div>

      <div className="OrderList__table-wrapper">
        {loading ? (
          <div className="OrderList__loading">Indlæser ordrer...</div>
        ) : orders.length === 0 ? (
          <div className="OrderList__empty">
            Ingen ordrer matcher de valgte filtre.
          </div>
        ) : (
          <>
            <table className="OrderList__table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="OrderList__checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      aria-label="Vælg alle ordrer"
                    />
                  </th>
                  <th>Konto-ID</th>
                  <th>Ordredato</th>
                  <th>Antal varer</th>
                  <th>Status</th>
                  <th>Sidst opdateret</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const isSelected = selectedId === order.id;
                  const isChecked = selectedIds.includes(order.id);

                  return (
                    <tr
                      key={order.id}
                      className={`OrderList__row ${isSelected ? "OrderList__row--selected" : ""}`}
                      onClick={() => onSelect(order.id)}
                    >
                      <td>
                        <input
                          type="checkbox"
                          className="OrderList__checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(order.id, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!isChecked && selectedIds.length >= MAX_SELECTION}
                          aria-label={`Vælg ordre ${order.id}`}
                        />
                      </td>
                      <td>{order.accountId}</td>
                      <td>{formatDate(order.orderDate || order.automaticOrderDate)}</td>
                      <td>{order.itemCount}</td>
                      <td>
                        <StatusBadge status={order.status as OrderStatus} />
                      </td>
                      <td>{formatDate(order.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="OrderList__pagination">
              <span className="OrderList__pagination-info">
                Side {page} af {totalPages} ({totalCount} ordrer)
              </span>
              <div className="OrderList__pagination-buttons">
                <button
                  className="OrderList__pagination-btn"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                >
                  Forrige
                </button>
                <button
                  className="OrderList__pagination-btn"
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                >
                  Næste
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
